import { useEffect, useState } from "react";
import { ChevronRight, Menu, X } from "lucide-react";
import {
  CANVA_URL,
  CHATEAU_URL,
  LANG_LABELS,
  LANGS,
  QUIZ_URL,
  translations,
  type Lang,
} from "./i18n";

function useLanguage() {
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("letwine-lang");
    if (saved === "en" || saved === "fr" || saved === "de" || saved === "es" || saved === "it") {
      return saved;
    }
    const nav = window.navigator.language.slice(0, 2);
    if (nav === "fr" || nav === "de" || nav === "es" || nav === "it") return nav;
    return "en";
  });

  useEffect(() => {
    window.localStorage.setItem("letwine-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return { lang, setLang };
}

function Logo() {
  return (
    <a href="#home" className="flex items-center gap-2" aria-label="Let.Wine">
      <img src="/logo.svg" alt="" aria-hidden="true" className="h-7 w-7 md:h-8 md:w-8" />
      <span className="text-lg font-extrabold tracking-tight text-ink md:text-xl">Let.Wine</span>
    </a>
  );
}

function LanguageSwitcher({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="flex items-center gap-1 rounded-full border border-ink/15 bg-white p-0.5">
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
            lang === l ? "bg-wine text-white" : "text-ink/60 hover:text-ink"
          }`}
        >
          {LANG_LABELS[l]}
        </button>
      ))}
    </div>
  );
}

function Nav({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  const [open, setOpen] = useState(false);
  const t = translations[lang];

  const links = [
    { href: "#chateau", label: t.nav.chateau },
    { href: "#parties", label: t.nav.parties },
    { href: "#personal", label: t.nav.personal },
    { href: "#about", label: t.nav.about },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-ink/10 bg-bg/90 backdrop-blur-md">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        <Logo />
        <div className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-ink/70 transition-colors hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher lang={lang} setLang={setLang} />
          <a
            href={CHATEAU_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-full bg-wine px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-wine/90 md:inline-block"
            data-testid="nav-cta"
          >
            {t.nav.cta}
          </a>
          <button
            type="button"
            className="lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="burger"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="border-t border-ink/10 bg-bg px-4 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-ink/80 transition-colors hover:bg-peach/20"
              >
                {l.label}
              </a>
            ))}
            <a
              href={CHATEAU_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-wine px-5 py-3 text-center text-sm font-semibold text-white"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ lang }: { lang: Lang }) {
  const t = translations[lang];

  return (
    <section
      id="home"
      className="relative flex min-h-[80vh] items-center justify-center overflow-hidden"
    >
      {/* Background image with overlay */}
      <picture>
        <source srcSet="/top-bg.webp" type="image/webp" />
        <img
          src="/top-bg.png"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>
      <div className="absolute inset-0 bg-ink/50" aria-hidden="true" />

      {/* Content */}
      <div className="relative mx-auto max-w-3xl px-4 py-20 text-center md:px-6 md:py-32">
        <h1 className="text-4xl font-light leading-tight tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
          {t.hero.h1}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base text-white/90 md:text-lg lg:text-xl">
          {t.hero.sub}
        </p>
        <a
          href={QUIZ_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-block rounded-full bg-wine px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-wine/90"
          data-testid="hero-cta"
        >
          {t.hero.cta}
        </a>
      </div>
    </section>
  );
}

interface SectionCardProps {
  id: string;
  title: string;
  desc: string;
  cta: string;
  href: string;
  isExternal?: boolean;
  variant?: "primary" | "secondary";
}

function SectionCard({
  id,
  title,
  desc,
  cta,
  href,
  isExternal = false,
  variant = "secondary",
}: SectionCardProps) {
  const cardClass = variant === "primary" ? "card primary" : "card";
  return (
    <section id={id} className="scroll-mt-20">
      <a
        href={href}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className={`${cardClass} group block rounded-2xl border border-ink/10 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md md:p-8`}
        data-testid={`section-${id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-ink md:text-2xl">{title}</h2>
            <p className="mt-2 text-sm text-ink/60 md:text-base">{desc}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-wine">
              {cta}
              <ChevronRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
      </a>
    </section>
  );
}

function Sections({ lang }: { lang: Lang }) {
  const t = translations[lang];

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-16 md:px-6 md:py-24">
      <SectionCard
        id="chateau"
        title={t.sections.chateau.title}
        desc={t.sections.chateau.desc}
        cta={t.sections.chateau.cta}
        href={CHATEAU_URL}
        isExternal
        variant="primary"
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SectionCard
          id="parties"
          title={t.sections.parties.title}
          desc={t.sections.parties.desc}
          cta={t.sections.parties.cta}
          href={CHATEAU_URL}
          isExternal
        />
        <SectionCard
          id="personal"
          title={t.sections.personal.title}
          desc={t.sections.personal.desc}
          cta={t.sections.personal.cta}
          href={QUIZ_URL}
          isExternal
        />
      </div>
      <SectionCard
        id="about"
        title={t.sections.about.title}
        desc={t.sections.about.desc}
        cta={t.sections.about.cta}
        href={CANVA_URL}
        isExternal
      />
    </div>
  );
}

function Footer({ lang }: { lang: Lang }) {
  const t = translations[lang];

  return (
    <footer className="bg-ink py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="" aria-hidden="true" className="h-7 w-7 md:h-8 md:w-8" />
            <span className="text-lg font-extrabold tracking-tight text-white md:text-xl">
              Let.Wine
            </span>
          </div>
          <p className="text-base text-white/70 md:text-lg">{t.footer.tagline}</p>
          <p className="text-xs text-white/40">{t.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const { lang, setLang } = useLanguage();
  return (
    <>
      <Nav lang={lang} setLang={setLang} />
      <main>
        <Hero lang={lang} />
        <Sections lang={lang} />
      </main>
      <Footer lang={lang} />
    </>
  );
}
